import { useQuery } from "@tanstack/react-query";
import { useAccount, useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import { zeroAddress, type Address } from "viem";
import { keelVaultAbi, keelVaultFactoryAbi } from "../abi";
import type { RedKeel } from "../config/networks";

export type Ocupada = {
  /** el vault MAS RECIENTE de esta wallet en esa pool */
  vault: Address;
  /** cuantos vaults tiene aqui en total */
  cuantos: number;
  /** cuantos de ellos tienen posicion abierta */
  vivas: number;
  /** uno con posicion abierta, para poder ofrecer abrirlo */
  viva?: Address;
  /** IMPIDE crear aqui. Solo en las factories de una sola piscina por pool. */
  bloquea: boolean;
};

export type Ocupacion = {
  /** la factory de esta red admite varias piscinas en la misma pool */
  multi: boolean;
  pools: Record<string, Ocupada>;
};

/**
 * Que pools tiene ya esta wallet en la factory donde se CREA, y si alguna impide crear otra.
 *
 * Hay dos generaciones de factory conviviendo y la pantalla se comporta distinto con cada una:
 *
 *  · MULTI_VAULT (la nueva): ninguna pool bloquea. Cada piscina es un vault nuevo y limpio, como en
 *    Autorange, y se pueden tener las que se quiera en la misma pool. Lo que sigue habiendo aqui es
 *    INFORMACION —"ya tienes 2 aqui"—, que no es lo mismo que un impedimento.
 *
 *  · Las anteriores: `createVault` graba `vaultFor[dueño][pool]`, revierte con `AlreadyHasVault()`
 *    si vuelves y no hay setter que lo borre. Ahi si hay que bloquear, pero SOLO el vault vivo: el
 *    vacio se reutiliza, porque `withdraw()` deja `tokenId = 0` y `open()` solo exige eso. Durante
 *    semanas se dio por hecho que una pool usada quedaba quemada para siempre, y no era verdad.
 */
export function useOcupadas(red: RedKeel | undefined, pools: readonly Address[] | undefined) {
  const { address } = useAccount();
  const config = useConfig();

  return useQuery({
    queryKey: ["ocupadas", red?.clave, address, pools?.length],
    enabled: !!red && !!address && !!pools?.length,
    staleTime: 60_000,
    queryFn: async (): Promise<Ocupacion> => {
      const client = getPublicClient(config, { chainId: red!.idCadena })!;

      const multi = await client
        .readContract({ address: red!.factory, abi: keelVaultFactoryAbi, functionName: "MULTI_VAULT" })
        .catch(() => false);

      // 1. los vaults de cada pool. Con `multi` son varios y hay que pedir la lista entera; sin el,
      //    `vaultFor` ya es el unico que puede haber.
      const res = await client.multicall({
        allowFailure: true,
        contracts: pools!.map((pool) =>
          multi
            ? {
                address: red!.factory,
                abi: keelVaultFactoryAbi,
                functionName: "vaultsForPool" as const,
                args: [address!, pool],
              }
            : {
                address: red!.factory,
                abi: keelVaultFactoryAbi,
                functionName: "vaultFor" as const,
                args: [address!, pool],
              }
        ),
      });

      const encontrados: Array<{ pool: string; vaults: Address[] }> = [];
      res.forEach((r, i) => {
        if (r.status !== "success") return;
        const lista = (Array.isArray(r.result) ? r.result : [r.result]).filter(
          (d): d is Address => !!d && d !== zeroAddress
        );
        if (lista.length) encontrados.push({ pool: pools![i]!.toLowerCase(), vaults: [...lista] });
      });
      if (!encontrados.length) return { multi, pools: {} };

      // 2. segunda vuelta: cual de esos vaults tiene posicion. Se piden todos de una vez y despues
      //    se reparten, en vez de un multicall por pool.
      const planos = encontrados.flatMap((e) => e.vaults);
      const ids = await client.multicall({
        allowFailure: true,
        contracts: planos.map((v) => ({
          address: v, abi: keelVaultAbi, functionName: "tokenId" as const,
        })),
      });

      // Si la lectura falla se asume ABIERTA: es el lado seguro. Marcar por error como reutilizable
      // un vault con dinero dentro llevaria al usuario a un revert al final del formulario, que es
      // justo lo que esta pantalla existe para evitar.
      const abiertos = new Map<Address, boolean>();
      planos.forEach((v, i) => {
        const r = ids[i];
        abiertos.set(v, r?.status !== "success" || r.result !== 0n);
      });

      const mapa: Record<string, Ocupada> = {};
      for (const e of encontrados) {
        const vivas = e.vaults.filter((v) => abiertos.get(v));
        mapa[e.pool] = {
          vault: e.vaults[e.vaults.length - 1]!,
          cuantos: e.vaults.length,
          vivas: vivas.length,
          viva: vivas[0],
          bloquea: !multi && vivas.length > 0,
        };
      }
      return { multi, pools: mapa };
    },
  });
}
